import React, { useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import {
    BreadCrumbs,
    LoadingBar,
    PageTitleWithToolTip
} from '../Common';
import Routes from '../../routes';
import MenuButton from '../MenuButton';
import UserInfo from '../../UserInfo';
import { canImportCustomerData } from './ImportCustomerData';
import { AppRoles } from '../../utils';
import useNavigate from '../../hooks/useNavigate';
import useBreadCrumbs from '../../hooks/useBreadCrumbs';

const Account = ({
    history: {
        push = () => { }
    } = {}
}) => {
    const [instanceName, setInstanceName] = useState(null);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate({
        pageName: 'Account'
    });
    useBreadCrumbs({
        path: { id: 'Account', text: 'Account Details', link: window.location.pathname, reset: true }
    });

    const menus = [
        {
            title: "Manage Your Account",
            icon: "/images/ic_accountdetails_2.png",
            isVisible: UserInfo.isSystemAdministrator() || (
                UserInfo.isNativeSystemOwner() && !(
                    UserInfo.isRegionalAdministrator()
                    || UserInfo.isTechnician()
                    || UserInfo.isTechnicianManager()
                    || UserInfo.isReadOnly()
                )
            ) || (
                    UserInfo.isNativeServiceProvider() && (
                        UserInfo.isTenantAdministrator()
                    )
                ),
            onClick: () => navigate.push(`/AccountDetails`),
        },
        {
            title: "Manage Your Users",
            icon: "/images/ic_users_2.png",
            isVisible: UserInfo.isRoleExist(AppRoles.View_Users_CurrentInstance),
            onClick: () => navigate.push(`/Users`)
        },
        {
            title: "View Your Documents",
            icon: "/images/ic_documents_2.png",
            // AB#16872: visible to everyone — system admins and cross-account roles
            // (e.g. service providers) need documents in non-native instances too;
            // every user can at least see sitewide "All Users" documents.
            isVisible: true,
            onClick: () => navigate.push(`/Documents`)
        },
        {
            title: "Manage your email alerts",
            icon: "/images/ic_email_alert.png",
            isVisible: UserInfo.isSystemAdministrator() || (UserInfo.isNativeSystemOwner() && (UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator())),
            onClick: () => navigate.push(`/EmailAlerts`)
        },
        {
            title: "Manage your regions and divisions",
            icon: "/images/ic_pin_2.png",
            isVisible: UserInfo.isSystemOwner() && UserInfo.isNativeAdminRole() && !UserInfo.isNativeAccountServiceProvider(),
            onClick: () => navigate.push(`/Regions`)
        },
        {
            title: "Manage your classifications",
            icon: "/images/ic_pin_3.png",
            isVisible: (UserInfo.isSystemAdministrator() ||
                (UserInfo.isSystemOwner() && UserInfo.isNativeUser() &&
                    (UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator())
                )
            ),
            onClick: () => navigate.push(`/Classifications`)
        },
        {
            title: "My Service Providers",
            icon: "/images/ic_wrench.png",
            // REMOVE UserInfo.isSystemAdministrator() BEFORE PUSHING LIVE
            isVisible: (UserInfo.isSuperTenantAdmin() || UserInfo.isNativeTenantAdministrator() || UserInfo.isSystemAdministrator()) && !UserInfo.isServiceProvider(),
            onClick: () => navigate.push(`/MyServiceProviders`)
        },
        {
            title: "Manage your system managers",
            icon: "/images/ic_system_manager.png",
            isVisible: true,
            onClick: () => navigate.push(`/SystemManagers`)
        },
        {
            title: "View material types",
            icon: "/images/ic_material_type.png",
            isVisible: UserInfo.isSystemAdministrator() || (UserInfo.isSystemOwner() && UserInfo.isNativeUser()),
            onClick: () => navigate.push(`/ViewMaterialType`)
        },
        {
            title: "My Service Providers",
            icon: "/images/ic_wrench_2.png",
            // REMOVE UserInfo.isSystemAdministrator() BEFORE PUSHING LIVE
            isVisible: (UserInfo.isSuperTenantAdmin() || UserInfo.isNativeTenantAdministrator() || UserInfo.isSystemAdministrator()) && !UserInfo.isServiceProvider(),
            onClick: () => navigate.push(`/MyServiceProviders`)
        },
        {
            title: "WebHook Logs",
            icon: "/images/ic_documents_2.png",
            isVisible: UserInfo.isSuperTenantAdmin() || UserInfo.isNativeTenantAdministrator() || UserInfo.isSystemAdministrator(),
            onClick: () => navigate.push('/WebHookLogs')
        },
        {
            title: "View material types",
            icon: "/images/ic_material_type.png",
            isVisible: UserInfo.isSystemAdministrator() || (UserInfo.isSystemOwner() && UserInfo.isNativeUser()),
            onClick: () => navigate.push(`/ViewMaterialType`)
        },
        {
            title: "Import Customer Data",
            icon: "/images/custom_import.svg",
            // canImportCustomerData() also requires the ImportCustomerData feature flag
            isVisible: canImportCustomerData(),
            onClick: () => navigate.push(`/ImportCustomerData`)
        }
    ];

    useEffect(() => {
        const fetchData = async () => {
            const response = await fetch(Routes.Account.GetInstanceInfo());
            const data = await response.json();
            setInstanceName(data.name);
            setLoading(false);
        };

        fetchData();
    }, []);

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                path={[{ text: 'Account Details', onClick: null }]}
            />
            <Grid
                container
                spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}
            >
                <Grid item xs={12}>
                    <PageTitleWithToolTip
                        pageTitle={`${instanceName} - Account Details`}
                    />
                </Grid>
                <Grid item xs={12}>
                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: "20px"
                    }}>
                        {menus.filter(x => x.isVisible).map(option => (
                            <MenuButton
                                key={option.title}
                                title={option.title}
                                icon={option.icon}
                                onClick={option.onClick}
                            />
                        ))}
                    </div>
                </Grid>
            </Grid>
        </>
    );

}

export default Account;
